import React, { useMemo } from 'react';
import './MiniBalanceOfPlay.css';
import { TeamColors } from '../../domain/entities';
import { getAwayColor } from '../../utils/theme';

interface MiniBalanceOfPlayProps {
    homeColors: TeamColors;
    awayColors: TeamColors;
    homeAttack: number;
    homeMid: number;
    homeDef: number;
    awayAttack: number;
    awayMid: number;
    awayDef: number;
}

export const MiniBalanceOfPlay: React.FC<MiniBalanceOfPlayProps> = (props) => {
    const {
        homeColors, awayColors,
        homeAttack, homeMid, homeDef,
        awayAttack, awayMid, awayDef
    } = props;

    const finalAwayColor = useMemo(() => {
        return getAwayColor(homeColors, awayColors);
    }, [homeColors, awayColors]);

    // Calculate overall balance based on midfield control + attack/defense differential
    // This mirrors the match simulation's logic for determining possession/chances
    const homeMidAdvantage = homeMid - awayMid;
    const homeAttackAdvantage = homeAttack - awayDef;
    const awayAttackAdvantage = awayAttack - homeDef;

    // Overall balance: midfield control is weighted heavily, with attack/defense as secondary factors
    const homeOverall = homeMid + (homeAttackAdvantage * 0.3) - (awayAttackAdvantage * 0.3);
    const awayOverall = awayMid + (awayAttackAdvantage * 0.3) - (homeAttackAdvantage * 0.3);

    const total = homeOverall + awayOverall;
    const homePercent = total > 0 ? Math.round((homeOverall / total) * 100) : 50;
    const awayPercent = 100 - homePercent;

    // Determine balance message
    let balanceMessage = 'Even';
    let balanceClass = 'even';

    if (homePercent >= 60) {
        balanceMessage = 'Dominant';
        balanceClass = 'dominant-home';
    } else if (homePercent >= 55) {
        balanceMessage = 'Strong';
        balanceClass = 'strong-home';
    } else if (homePercent >= 52) {
        balanceMessage = 'Edge';
        balanceClass = 'edge-home';
    } else if (awayPercent >= 60) {
        balanceMessage = 'Dominated';
        balanceClass = 'dominated-away';
    } else if (awayPercent >= 55) {
        balanceMessage = 'Under Pressure';
        balanceClass = 'pressure-away';
    } else if (awayPercent >= 52) {
        balanceMessage = 'Slight Disadvantage';
        balanceClass = 'edge-away';
    }

    return (
        <div className="mini-balance-of-play">
            <div className="mini-balance-header">
                <span className="mini-balance-label">Balance of Play</span>
                <span className={`mini-balance-status ${balanceClass}`}>{balanceMessage}</span>
            </div>
            <div className="mini-balance-bar">
                <div
                    className="mini-bar-segment home"
                    style={{ width: `${homePercent}%`, backgroundColor: homeColors.primary }}
                    title={`Your team controlled ${homePercent}% of the match`}
                >
                    {homePercent >= 15 && <span className="mini-bar-value">{homePercent}%</span>}
                </div>
                <div
                    className="mini-bar-segment away"
                    style={{ width: `${awayPercent}%`, backgroundColor: finalAwayColor }}
                    title={`Opposition controlled ${awayPercent}% of the match`}
                >
                    {awayPercent >= 15 && <span className="mini-bar-value">{awayPercent}%</span>}
                </div>
            </div>
        </div>
    );
};